"use client";

import React from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { cn } from "@/lib/utils/cn";
import {
  MessageSquare,
  BookOpen,
  GraduationCap,
  Calendar,
  Briefcase,
  Users,
  Bell,
  FileText,
  Clock,
} from "lucide-react";

export interface ActivityItem {
  id: string;
  type: "discussion" | "article" | "news" | "course" | "event" | "job" | "connection" | "notification";
  title: string;
  description: string;
  timestamp: string;
  icon: React.ElementType;
  color: string;
}

interface ActivityFeedProps {
  activities: ActivityItem[];
  isLoading?: boolean;
}

const typeIcons: Record<string, React.ElementType> = {
  discussion: MessageSquare,
  article: BookOpen,
  news: FileText,
  course: GraduationCap,
  event: Calendar,
  job: Briefcase,
  connection: Users,
  notification: Bell,
};

export function ActivityFeed({ activities, isLoading }: ActivityFeedProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-muted/10" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-3/4 bg-muted/10 rounded" />
              <div className="h-3 w-1/2 bg-muted/10 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <EmptyState
        title="No recent activity"
        description="Your activity feed will appear here as you interact with the platform."
        icon={<Clock className="h-8 w-8 text-muted-foreground/40" />}
      />
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity) => {
        const Icon = typeIcons[activity.type] || MessageSquare;

        return (
          <div key={activity.id} className="flex items-start gap-3">
            <div className={cn("mt-0.5 rounded-full bg-muted/10 p-2 text-muted-foreground")}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{activity.title}</p>
              <p className="text-xs text-muted-foreground truncate">{activity.description}</p>
              <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {activity.timestamp}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function getActivityIcon(type: ActivityItem["type"]) {
  return typeIcons[type] || MessageSquare;
}
